// utils/crewUtils.js
import { ROUTE_CONFIG } from '../config/routeConfig';
import moment from 'moment';
import { isEven, findNextAppropriateVisitDate } from '../utils/dateUtils';


/**
 * Filter crews based on search term
 */
export const filterCrews = (crews, searchTerm) => {
  if (!searchTerm) return crews;
  
  const lowerSearch = searchTerm.toLowerCase();
  return crews.filter(crew => 
    crew.name?.toLowerCase().includes(lowerSearch) ||
    crew.foremanName?.toLowerCase().includes(lowerSearch) ||
    crew.number?.toString().includes(searchTerm)
  );
};

/**
 * Calculate total hours for a crew on a specific day
 */
export const calculateCrewDayHours = (weeklyHours, crewId, dayOfWeek) => {
  // console.log('calculateCrewDayHours....', weeklyHours, crewId, dayOfWeek)
  const dayData = weeklyHours[crewId]?.[dayOfWeek] || { serviceTime: 0, driveTime: 0 };
  // console.log("Dat Data...", dayData)
  return {
    service: Number(dayData.serviceTime) || 0,
    drive: Number(dayData.driveTime) || 0,
    total: Number(dayData.serviceTime || 0) + Number(dayData.driveTime || 0)
  };
};

/**
 * Calculate weekly totals for a crew
 */
export const calculateCrewWeeklyHours = (weeklyHours, crewId) => {
  const weekData = weeklyHours[crewId]?.total || { serviceTime: 0, driveTime: 0 };
  return {
    service: Number(weekData.serviceTime) || 0,
    drive: Number(weekData.driveTime) || 0,
    total: Number(weekData.serviceTime || 0) + Number(weekData.driveTime || 0)
  };
};

/**
 * Get crew workload status
 */
export const getCrewWorkloadStatus = (totalHours) => {
  const { OPTIMIZATION } = ROUTE_CONFIG;
  
  if (totalHours <= OPTIMIZATION.IDEAL_DAILY_HOURS_MIN) {
    return { status: 'underutilized', color: 'info' };
  } else if (totalHours <= OPTIMIZATION.IDEAL_DAILY_HOURS_MAX) {
    return { status: 'optimal', color: 'success' };
  } else if (totalHours <= OPTIMIZATION.OVERTIME_THRESHOLD_HOURS) {
    return { status: 'busy', color: 'warning' };
  } else {
    return { status: 'overtime', color: 'error' };
  }
};

/**
 * Sort crews by various methods
 */
// export const sortCrews = (crews, method = 'name', direction = 'asc') => {
//   const sorted = [...crews].sort((a, b) => {
//     let comparison = 0;
    
//     switch (method) {
//       case 'name':
//         comparison = (a.name || '').localeCompare(b.name || '');
//         break;
//       case 'foreman':
//         comparison = (a.foremanName || '').localeCompare(b.foremanName || '');
//         break;
//       case 'number':
//         comparison = (a.number || 0) - (b.number || 0);
//         break;
//       default:
//         comparison = 0;
//     }
    
//     return direction === 'asc' ? comparison : -comparison;
//   });
  
//   return sorted;
// };

/**
 * Format crew name with foreman
 */
export const formatCrewName = (crew) => {
  if (!crew) return 'Unassigned';
  return `${crew.name}${crew.foremanName ? ` (${crew.foremanName})` : ''}`;
};

/**
 * Check if crew can take additional work
 */
export const canCrewTakeWork = (weeklyHours, crewId, dayOfWeek, additionalHours) => {
  const currentHours = calculateCrewDayHours(weeklyHours, crewId, dayOfWeek);
  const totalWithNew = currentHours.total + additionalHours;
  return totalWithNew <= ROUTE_CONFIG.OPTIMIZATION.MAX_DAILY_HOURS;
};

/**
 * Get crew efficiency score
 */
export const getCrewEfficiency = (crew, weeklyHours) => {
  const weekData = calculateCrewWeeklyHours(weeklyHours, crew._id);
  if (weekData.total === 0) return 0;
  
  // Efficiency = service time / total time
  return Math.round((weekData.service / weekData.total) * 100);
};

/**
 * Find best crew for a job based on availability
 */
export const findBestCrewForJob = (crews, job, weeklyHours, dayOfWeek) => {
  const availableCrews = crews.filter(crew => 
    canCrewTakeWork(weeklyHours, crew._id, dayOfWeek, job.estimatedHours)
  );
  
  if (availableCrews.length === 0) return null;
  
  // Sort by current workload (least busy first)
  return availableCrews.sort((a, b) => {
    const aHours = calculateCrewDayHours(weeklyHours, a._id, dayOfWeek).total;
    const bHours = calculateCrewDayHours(weeklyHours, b._id, dayOfWeek).total;
    return aHours - bHours;
  })[0];
};

/**
 * Calculate crew utilization percentage
 */
export const calculateCrewUtilization = (weeklyHours, crewId) => {
  const weekData = calculateCrewWeeklyHours(weeklyHours, crewId);
  const maxWeeklyHours = ROUTE_CONFIG.OPTIMIZATION.MAX_DAILY_HOURS * 5; // 5 working days
  return Math.round((weekData.total / maxWeeklyHours) * 100);
};

// Enhanced set of 25 visually distinct colors
const distinctHexColors = [
  "#E41A1C", // Red
  "#377EB8", // Blue
  "#4DAF4A", // Green
  "#984EA3", // Purple
  "#FF7F00", // Orange
  "#FFFF33", // Yellow
  "#A65628", // Brown
  "#F781BF", // Pink
  "#1B9E77", // Teal
  "#D95F02", // Burnt orange
  "#7570B3", // Slate blue
  "#E7298A", // Magenta
  "#66A61E", // Olive green
  "#E6AB02", // Gold
  "#A6761D", // Dark tan
  "#666666", // Dark gray
  "#1F78B4", // Medium blue
  "#B2DF8A", // Light green
  "#33A02C", // Dark green
  "#FB9A99", // Light red
  "#E31A1C", // Bright red
  "#FDBF6F", // Light orange
  "#FF7F00", // Bright orange
  "#CAB2D6", // Light purple
  "#6A3D9A"  // Dark purple
];

/**
 * Determines if text should be black or white based on background color
 * Uses YIQ formula for perceived brightness calculation
 */
export const getTextColorForBackground = (hexColor) => {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return yiq >= 128 ? "#000000" : "#FFFFFF";
};

// In crewUtils.js - Add this enhanced version:
export const getCrewColor = (index) => {
  const colors = ROUTE_CONFIG.BUSINESS_RULES.CREW_COLOR_PALETTE;
  const colorConfig = colors[index % colors.length];
  
  // If it already has textColor calculated, return as-is
  if (colorConfig.textColor) {
    return colorConfig;
  }
  
  // Otherwise calculate the text color
  const textColor = getTextColorForBackground(colorConfig.backgroundColor);
  return {
    backgroundColor: colorConfig.backgroundColor,
    textColor
  };
};

/**
 * Sort crews by various criteria
 */
export const sortCrews = (crews, sortMethod, sortDirection) => {
  // console.log('Sort crews', crews, sortMethod, sortDirection);
  return [...crews].sort((a, b) => {
    let aValue, bValue;
    switch (sortMethod) {
      case 'todayServiceHours':
        aValue = (a.todayData?.totalServiceTime || 0) / 60;
        bValue = (b.todayData?.totalServiceTime || 0) / 60;
        break;
      case 'todayTotalHours':
        aValue = ((a.todayData?.totalServiceTime || 0) + (a.todayData?.totalDriveTime || 0)) / 60;
        bValue = ((b.todayData?.totalServiceTime || 0) + (b.todayData?.totalDriveTime || 0)) / 60;
        break;
      case 'weekTotalServiceHours':
        aValue = (a.weekData?.totalServiceTime || 0) / 60;
        bValue = (b.weekData?.totalServiceTime || 0) / 60;
        break;
      case 'weekTotalHours':
        aValue = ((a.weekData?.totalServiceTime || 0) + (a.weekData?.totalDriveTime || 0)) / 60;
        bValue = ((b.weekData?.totalServiceTime || 0) + (b.weekData?.totalDriveTime || 0)) / 60;
        break;
      default:
        aValue = a.name;
        bValue = b.name;
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};

/**
 * Get color for a crew
 */
export const getColor = (crewId, crews) => {
  let color = '#fff';
  if (crews?.length) {
    const crew = crews.find(crewObj => (crewObj._id === crewId));
    if (crew?.color) color = crew.color;
  }
  return color;
};
